import { SECONDS_PER_DAY, utcDay } from "@highmarch/domain";
import { webDevice } from "../infra/adapters";
import { copyDiagnostics } from "../infra/diagnostics";
import { isNativeShell } from "../infra/platform/shell";
import { offlineState, offlineStateText } from "../infra/offlineCache";
import { saveStore } from "../infra/save/openSaveStore";
import { extraReminders, planReminders, reminders } from "../infra/platform/reminders";
import { sandboxStore } from "../infra/store/openStore";
import { SANDBOX_OUTCOMES } from "../infra/store/sandboxStore";
import { PRODUCT_LISTINGS } from "../infra/store/products";
import { jobTargetName } from "../ui/hub/awayCard";
import { icon } from "../ui/iconsKit";
import { showLegal } from "../ui/legalSheet";
import { showSandboxStore } from "../ui/sandboxStoreSheet";
import { SaveNotices } from "../ui/saveNotices";
import { toast } from "../ui/toast";
import { Feature, FeatureContext, MORE_ORDER } from "./registry";

/**
 * Platform, save and compliance (FINISH_PLAN WP4): Settings → Credits & legal, Copy diagnostics and (iOS) Reminders;
 * the save store's notices (storage full, an unreadable save, another tab took over); local reminders scheduled as the
 * app goes to the background; and, in debug builds on the web, More → Sandbox store.
 */

/** Settings row order: after the core rows, in this order (features/registry.ts SettingsRow). */
const SETTINGS_ORDER = { reminders: 40, offline: 70, diagnostics: 80, legal: 90 } as const;

const chevBtn = (cls: string, label: string) => `<button class="btn dark ${cls}"><span>${label}</span>${icon("chev", 16)}</button>`;
const toggleHtml = (on: boolean) => `<button class="toggle ${on ? "on" : ""}" role="switch" aria-checked="${on}"><span class="knob"></span></button>`;

export const platformFeature: Feature = {
  id: "platform",
  register(ctx, registry) {
    registry.settingsRow({
      id: "legal", order: SETTINGS_ORDER.legal, scope: "everywhere",
      title: () => "About & credits", sub: () => "The makers, their licences, privacy policy",
      control: () => chevBtn("legal-open", "Open"),
      bind: (row, api) => { row.querySelector<HTMLButtonElement>(".legal-open")!.onclick = () => showLegal(api.parent); },
    });
    // The web app's offline copy: installed or not, and how much of the game is on the device (infra/offlineCache.ts).
    if (!isNativeShell() && import.meta.env.PROD) {
      registry.settingsRow({
        id: "offline", order: SETTINGS_ORDER.offline, scope: "everywhere",
        title: () => "Play offline", sub: () => "",
        control: () => `<span class="offline-state">…</span>`,
        bind: (row) => {
          const out = row.querySelector<HTMLElement>(".offline-state")!;
          void offlineState().then((s) => { out.textContent = offlineStateText(s); });
        },
      });
    }
    registry.settingsRow({
      id: "diagnostics", order: SETTINGS_ORDER.diagnostics, scope: "everywhere",
      title: () => "Report a bug", sub: () => "Copies a short field report to send us",
      control: () => `<button class="btn dark diag-copy"><span>Copy</span></button>`,
      bind: (row, api) => {
        row.querySelector<HTMLButtonElement>(".diag-copy")!.onclick = async () => {
          const how = await copyDiagnostics();
          if (how === "copied") toast(api.parent, "Report copied: paste it into your message");
          else if (how === "failed") toast(api.parent, "Couldn't copy the report. Try again");
        };
      },
    });
    if (reminders.available) {
      registry.settingsRow({
        id: "reminders", order: SETTINGS_ORDER.reminders, scope: "menu",
        title: () => "Reminders", sub: () => "When an upgrade finishes or new orders arrive",
        control: () => toggleHtml(reminders.enabled),
        bind: (row, api) => {
          row.querySelector<HTMLButtonElement>(".toggle")!.onclick = async () => {
            const on = await reminders.setEnabled(!reminders.enabled);
            if (!on && !reminders.enabled) toast(api.parent, "Reminders are off: allow them in iOS Settings → Notifications");
            api.rerender();
          };
        },
      });
      bindReminders(ctx);
    }
    const sandbox = sandboxStore();
    if (ctx.debug && sandbox) {
      registry.moreRow({
        id: "sandboxstore", order: MORE_ORDER.debug - 1, icon: "shop",
        label: () => "Sandbox store", note: () => "Test purchases, never charged",
        open: (hub) => { showSandboxStore(hub.parent, { store: sandbox, outcomes: SANDBOX_OUTCOMES, listings: PRODUCT_LISTINGS }); },
      });
    }
    bindSaveNotices(ctx);
  },
};

/** The save store's problems, as notices: the ones met while the save opened (shown once the loading screen is gone), then live. */
function bindSaveNotices(ctx: FeatureContext): void {
  const store = saveStore();
  if (!store) return;
  const notices = new SaveNotices(ctx.ui, {
    backupText: () => { ctx.persist(); return JSON.stringify(ctx.save); },
    damagedText: (key) => store.load(key),
    share: (text) => webDevice.shareText("Highmarch progress", text),
    reload: () => location.reload(),
  });
  const early = [...store.problems];
  store.onProblem((p) => notices.show(p));
  if (early.length === 0) return;
  // The boot's problems wait for the boot's loading plate to go, so the notice is not hidden behind it.
  const PollMs = 500, GiveUpMs = 120_000, started = Date.now();
  const poll = () => {
    if (ctx.ui.querySelector(".screen.title.booting") && Date.now() - started < GiveUpMs) { setTimeout(poll, PollMs); return; }
    for (const p of early) notices.show(p);
  };
  setTimeout(poll, PollMs);
}

/** Reminders are scheduled as the app goes to the background and cleared when it comes back. */
function bindReminders(ctx: FeatureContext): void {
  const plan = () => {
    const now = ctx.now();
    return planReminders({
      now,
      upgrades: ctx.catalog.profile.jobs.map((j) => ({ id: j.id, endAt: j.endAt, name: jobTargetName(ctx.balance, j) })),
      ordersResetAt: ctx.catalog.highestLevelCleared >= 1 ? (utcDay(now) + 1) * SECONDS_PER_DAY : null,
      extra: extraReminders(now),
    }, new Date().getTimezoneOffset());
  };
  const sync = () => { if (document.hidden) void reminders.schedule(plan()); else void reminders.clear(); };
  document.addEventListener("visibilitychange", sync);
  void reminders.clear();
}
